import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import EditIcon from '@mui/icons-material/Edit';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import { Box, Tooltip, Typography } from '@mui/material';
import Chip from '@mui/material/Chip';
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridRowParams,
  GridSlotsComponentsProps,
  Toolbar,
  ToolbarButton,
} from '@mui/x-data-grid';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DialogProps, useDialogs } from '@toolpad/core/useDialogs';
import dayjs from 'dayjs';

import { getStudentLedgerOptions } from '@drivingdoc/api-client/generated/react-query';
import { LedgerEntry } from 'types/students';

import ChargeDialog from 'components/Dialogs/Finance/ChargeDialog';
import PaymentDialog from 'components/Dialogs/Finance/PaymentDialog';
import RefundDialog from 'components/Dialogs/Finance/RefundDialog';
import VoidChargeDialog from 'components/Dialogs/Finance/VoidDialogs/VoidChargeDialog';
import VoidPaymentDialog from 'components/Dialogs/Finance/VoidDialogs/VoidPaymentDialog';
import VoidRefundDialog from 'components/Dialogs/Finance/VoidDialogs/VoidRefundDialog';

import { formatCents } from 'utils/formatCurrency';

import './styles.css';

declare module '@mui/x-data-grid' {
  interface FooterPropsOverrides {
    balance: string;
  }
}

interface LedgerProps {
  studentId: string;
}

export default function Ledger({ studentId }: LedgerProps) {
  // LicenseInfo.setLicenseKey(
  //   '0bc1f2c598450714f08031134df5353fT1JERVI6NDEwNjEsRVhQSVJZPTE2ODA2MjIyOTEwMDAsS0VZVkVSU0lPTj0x',
  // );
  const dialogs = useDialogs();
  const queryClient = useQueryClient();

  const studentLedger = useQuery({
    ...getStudentLedgerOptions({
      path: {
        id: studentId,
      },
      query: {
        showVoided: true,
      },
    }),
  });
  const ledgerRows = (studentLedger.data as unknown as LedgerEntry[] | undefined) ?? [];

  const refreshLedger = () => {
    queryClient.invalidateQueries({ queryKey: ['student_ledger'] });
  };

  const CustomToolbar = () => {
    return (
      <Toolbar style={{ justifyContent: 'flex-start' }}>
        <Tooltip title="Create Charge">
          <ToolbarButton
            onClick={() => {
              dialogs.open(ChargeDialog, { studentId: studentId }).then(() => refreshLedger());
            }}
          >
            <AttachMoneyIcon fontSize="medium" color="error" />
          </ToolbarButton>
        </Tooltip>

        <Tooltip title="Create Payment">
          <ToolbarButton
            onClick={() => {
              dialogs.open(PaymentDialog, { studentId: studentId }).then(() => refreshLedger());
            }}
          >
            <AttachMoneyIcon fontSize="medium" color="success" />
          </ToolbarButton>
        </Tooltip>

        <Tooltip title="Create Refund">
          <ToolbarButton
            onClick={() => {
              dialogs.open(RefundDialog, { studentId: studentId }).then(() => refreshLedger());
            }}
          >
            <CurrencyExchangeIcon fontSize="medium" color="primary" />
          </ToolbarButton>
        </Tooltip>
      </Toolbar>
    );
  };

  const CustomFooter = (props: NonNullable<GridSlotsComponentsProps['footer']>) => {
    return (
      <Box sx={{ pr: 4, pb: 1, display: 'flex', justifyContent: 'flex-end' }}>
        <Typography variant="h5">Balance: {props.balance}</Typography>
      </Box>
    );
  };

  const handleEdit = (params: GridRowParams) => {
    // ensure row has a valid id
    if (!params.id) {
      // captureException(new Error('Row missing id'));
      return;
    }

    // determine which dialog to open
    if (params.row.type === 'charge') {
      dialogs.open(ChargeDialog, { chargeId: params?.id as string }).then(() => refreshLedger());
    } else if (params.row.type === 'payment') {
      dialogs.open(PaymentDialog, { paymentId: params?.id as string }).then(() => refreshLedger());
    } else if (params.row.type === 'refund') {
      dialogs.open(RefundDialog, { refundId: params?.id as string }).then(() => refreshLedger());
    } else {
      throw new Error(`Unknown entry type: ${params.row.type}`);
    }
  };

  const handleVoid = (params: GridRowParams) => {
    // ensure row has a valid id
    if (!params.id) {
      // captureException(new Error('Row missing id'));
      return;
    }

    // void dialog payload
    const payload = {
      id: params.id as string,
    };

    // create map of dialogs
    const dialogMap: Record<string, React.ComponentType<DialogProps<{ id: string }>>> = {
      charge: VoidChargeDialog,
      payment: VoidPaymentDialog,
      refund: VoidRefundDialog,
    };

    // determine which dialog to open
    const DialogComponent = dialogMap[params.row.type];

    if (!DialogComponent) {
      throw new Error(`Unknown entry type: ${params.row.type}`);
    }

    // open the dialog
    dialogs.open(DialogComponent, payload, {
      onClose: async () => refreshLedger(),
    });
  };

  const columns: GridColDef[] = [
    {
      field: 'date',
      headerName: 'Date',
      type: 'date',
      width: 130,
      sortable: false,
      valueFormatter: (value) => {
        if (value === null) return null;
        return dayjs(value as string).format('MM/DD/YYYY');
      },
    },
    {
      field: 'type',
      headerName: 'Type',
      type: 'singleSelect',
      sortable: false,
      width: 130,
      renderCell: (params) => {
        switch (params.value) {
          case 'charge':
            return (
              <Chip
                sx={{ width: '100%!important', fontWeight: 'bold' }}
                // sx={{ fontWeight: 'bold' }}
                icon={<AttachMoneyIcon fontSize="medium" />}
                size="small"
                label="Charge"
                color="error"
                variant="outlined"
              />
            );
          case 'payment':
            return (
              <Chip
                sx={{ width: '100%!important', fontWeight: 'bold' }}
                // sx={{ fontWeight: 'bold' }}
                icon={<AttachMoneyIcon fontSize="medium" />}
                size="small"
                label="Payment"
                color="success"
                variant="outlined"
              />
            );
          case 'refund':
            return (
              <Chip
                sx={{ width: '100%!important', fontWeight: 'bold' }}
                // sx={{ fontWeight: 'bold' }}
                icon={<AttachMoneyIcon fontSize="medium" />}
                size="small"
                label="Refund"
                color="primary"
                variant="outlined"
              />
            );
        }
      },
    },
    {
      field: 'memo',
      headerName: 'Memo',
      type: 'string',
      // width: 130,
      flex: 1,
      sortable: false,
    },
    {
      field: 'payment_method',
      headerName: 'Method',
      // type: 'string',
      width: 130,
      sortable: false,
      valueGetter: (_value, row) => {
        return row?.payment_method?.name ?? '';
      },
    },
    {
      field: 'debit',
      headerName: 'Debit',
      type: 'string',
      width: 130,
      sortable: false,
      valueFormatter: (value) => (value ? formatCents(value) : ''),
    },
    {
      field: 'credit',
      headerName: 'Credit',
      type: 'string',
      width: 130,
      sortable: false,
      valueFormatter: (value) => (value ? formatCents(value) : ''),
    },
    {
      field: 'balance',
      headerName: 'Balance',
      type: 'string',
      width: 130,
      sortable: false,
      valueFormatter: (value, row) => (row?.voided ? '' : formatCents(value)),
    },
    {
      field: 'actions',
      type: 'actions',
      width: 80,
      getActions: (params: GridRowParams) => [
        <GridActionsCellItem key={params.id} icon={<EditIcon fontSize="large" />} label="Edit" onClick={() => handleEdit(params)} />,
        <GridActionsCellItem
          key={params.id}
          icon={<MoneyOffIcon />}
          label="Void"
          onClick={async () => handleVoid(params)}
          showInMenu
        />,
      ],
    },
  ];

  return (
    <>
      <DataGrid
        disableColumnMenu
        columns={columns}
        loading={studentLedger.isLoading}
        rows={ledgerRows}
        onRowClick={handleEdit}
        showToolbar
        slots={{
          toolbar: CustomToolbar,
          footer: CustomFooter,
        }}
        slotProps={{
          footer: {
            balance: formatCents(ledgerRows?.[ledgerRows.length - 1]?.balance ?? 0),
          },
        }}
        getRowClassName={(params) => {
          return params.row.voided ? 'voided-row' : '';
        }}
        sx={{
          '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
            outline: 'none',
          },
          '& .MuiInputBase-root': {
            width: 'auto!important',
          },
        }}
      />
    </>
  );
}
